import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	TelematicsMileageClient,
	RateTopUpMilesCommand,
	RateTopUpMilesResponse,
	CancelTopUpMilesCommand,
	MakePaymentIntentCommand,
	MakePaymentIntentResponse,
	TakeUpCommand,
	TakeUpResponse,
} from '@acorn-portal/shared/src/services/telematicsMileageApi';

interface TelematicsTopUpMilesState {
	// Rate response
	rateResponse: RateTopUpMilesResponse | null;
	rateLoading: boolean;
	rateError: string | null;
	// Cancel
	cancelLoading: boolean;
	cancelError: string | null;
	cancelSuccess: boolean;
	// Payment intent
	paymentIntent: MakePaymentIntentResponse | null;
	paymentIntentLoading: boolean;
	paymentIntentError: string | null;
	// Take up
	takeUpResponse: TakeUpResponse | null;
	takeUpLoading: boolean;
	takeUpError: string | null;
}

const initialState: TelematicsTopUpMilesState = {
	rateResponse: null,
	rateLoading: false,
	rateError: null,
	cancelLoading: false,
	cancelError: null,
	cancelSuccess: false,
	paymentIntent: null,
	paymentIntentLoading: false,
	paymentIntentError: null,
	takeUpResponse: null,
	takeUpLoading: false,
	takeUpError: null,
};

export const rateTopUpMiles = createAsyncThunk<RateTopUpMilesResponse, RateTopUpMilesCommand, { rejectValue: unknown }>(
	'telematicsTopUpMiles/rate',
	async (command: RateTopUpMilesCommand, { rejectWithValue }) => {
		try {
			const client = new TelematicsMileageClient(undefined, API);
			const response = await client.rate(command);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const cancelTopUpMiles = createAsyncThunk<boolean, CancelTopUpMilesCommand, { rejectValue: unknown }>(
	'telematicsTopUpMiles/cancel',
	async (command: CancelTopUpMilesCommand, { rejectWithValue }) => {
		try {
			const client = new TelematicsMileageClient(undefined, API);
			await client.cancel(command);
			return true;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const makePaymentIntent = createAsyncThunk<
	MakePaymentIntentResponse,
	MakePaymentIntentCommand,
	{ rejectValue: unknown }
>('telematicsTopUpMiles/paymentIntent', async (command: MakePaymentIntentCommand, { rejectWithValue }) => {
	try {
		const client = new TelematicsMileageClient(undefined, API);
		const response = await client.intent(command);
		return response;
	} catch (e) {
		if (axios.isAxiosError(e) && e.response) {
			if (e.response.status < 200 || e.response.status > 300) {
				return rejectWithValue(e);
			}
		}
		if (typeThrownException(e) && e.status) {
			if (e.status < 200 || e.status > 300) {
				return rejectWithValue(e);
			}
		}
		throw e;
	}
});

export const takeUpTopUpMiles = createAsyncThunk<TakeUpResponse, TakeUpCommand, { rejectValue: unknown }>(
	'telematicsTopUpMiles/takeUp',
	async (command: TakeUpCommand, { rejectWithValue }) => {
		try {
			const client = new TelematicsMileageClient(undefined, API);
			const response = await client.takeup(command);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const telematicsTopUpMilesSlice = createSlice({
	name: 'telematicsTopUpMiles',
	initialState,
	reducers: {
		setRateResponse: (state: TelematicsTopUpMilesState, action: PayloadAction<RateTopUpMilesResponse>) => {
			state.rateResponse = action.payload;
		},
		setPaymentIntent: (state: TelematicsTopUpMilesState, action: PayloadAction<MakePaymentIntentResponse>) => {
			state.paymentIntent = action.payload;
		},
		setTakeUpResponse: (state: TelematicsTopUpMilesState, action: PayloadAction<TakeUpResponse>) => {
			state.takeUpResponse = action.payload;
		},
		clearRateResponse: (state: TelematicsTopUpMilesState) => {
			state.rateResponse = null;
			state.rateLoading = false;
			state.rateError = null;
		},
		clearCancelState: (state: TelematicsTopUpMilesState) => {
			state.cancelLoading = false;
			state.cancelError = null;
			state.cancelSuccess = false;
		},
		clearPaymentIntent: (state: TelematicsTopUpMilesState) => {
			state.paymentIntent = null;
			state.paymentIntentLoading = false;
			state.paymentIntentError = null;
		},
		clearTakeUpResponse: (state: TelematicsTopUpMilesState) => {
			state.takeUpResponse = null;
			state.takeUpLoading = false;
			state.takeUpError = null;
		},
		clearAll: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<TelematicsTopUpMilesState>) => {
		// Rate
		builder.addCase(rateTopUpMiles.pending, (state: TelematicsTopUpMilesState) => {
			state.rateLoading = true;
			state.rateError = null;
		});
		builder.addCase(rateTopUpMiles.fulfilled, (state: TelematicsTopUpMilesState, { payload }) => {
			state.rateLoading = false;
			state.rateResponse = payload;
		});
		builder.addCase(rateTopUpMiles.rejected, (state: TelematicsTopUpMilesState, action) => {
			state.rateLoading = false;
			state.rateError = action.error.message ?? 'Failed to rate mileage top-up';
		});

		// Cancel
		builder.addCase(cancelTopUpMiles.pending, (state: TelematicsTopUpMilesState) => {
			state.cancelLoading = true;
			state.cancelError = null;
			state.cancelSuccess = false;
		});
		builder.addCase(cancelTopUpMiles.fulfilled, (state: TelematicsTopUpMilesState) => {
			state.cancelLoading = false;
			state.cancelSuccess = true;
			// Clear the rate response since transaction was cancelled
			state.rateResponse = null;
		});
		builder.addCase(cancelTopUpMiles.rejected, (state: TelematicsTopUpMilesState, action) => {
			state.cancelLoading = false;
			state.cancelError = action.error.message ?? 'Failed to cancel mileage top-up';
		});

		// Payment Intent
		builder.addCase(makePaymentIntent.pending, (state: TelematicsTopUpMilesState) => {
			state.paymentIntentLoading = true;
			state.paymentIntentError = null;
		});
		builder.addCase(makePaymentIntent.fulfilled, (state: TelematicsTopUpMilesState, { payload }) => {
			state.paymentIntentLoading = false;
			state.paymentIntent = payload;
		});
		builder.addCase(makePaymentIntent.rejected, (state: TelematicsTopUpMilesState, action) => {
			state.paymentIntentLoading = false;
			state.paymentIntentError = action.error.message ?? 'Failed to create payment intent';
		});

		// Take Up
		builder.addCase(takeUpTopUpMiles.pending, (state: TelematicsTopUpMilesState) => {
			state.takeUpLoading = true;
			state.takeUpError = null;
		});
		builder.addCase(takeUpTopUpMiles.fulfilled, (state: TelematicsTopUpMilesState, { payload }) => {
			state.takeUpLoading = false;
			state.takeUpResponse = payload;
		});
		builder.addCase(takeUpTopUpMiles.rejected, (state: TelematicsTopUpMilesState, action) => {
			state.takeUpLoading = false;
			state.takeUpError = action.error.message ?? 'Failed to complete mileage top-up';
		});
	},
});

export const {
	setRateResponse,
	setPaymentIntent,
	setTakeUpResponse,
	clearRateResponse,
	clearCancelState,
	clearPaymentIntent,
	clearTakeUpResponse,
	clearAll,
} = telematicsTopUpMilesSlice.actions;
export default telematicsTopUpMilesSlice.reducer;
